import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type UserRole = 'owner' | 'admin' | 'manager' | 'staff' | 'viewer';

export interface RolePermissions {
  canManageUsers: boolean;
  canManageUnits: boolean;
  canManageTemplates: boolean;
  canViewReports: boolean;
  canCreateChecklists: boolean;
  canManageIncidents: boolean;
  canViewAuditTrail: boolean;
  canManageCompliance: boolean;
  canExportData: boolean;
  canManageSettings: boolean;
}

const ROLE_PERMISSIONS: Record<UserRole, RolePermissions> = {
  owner: {
    canManageUsers: true,
    canManageUnits: true,
    canManageTemplates: true,
    canViewReports: true,
    canCreateChecklists: true,
    canManageIncidents: true,
    canViewAuditTrail: true,
    canManageCompliance: true,
    canExportData: true,
    canManageSettings: true,
  },
  admin: {
    canManageUsers: true,
    canManageUnits: true,
    canManageTemplates: true,
    canViewReports: true,
    canCreateChecklists: true,
    canManageIncidents: true,
    canViewAuditTrail: true,
    canManageCompliance: true,
    canExportData: true,
    canManageSettings: false,
  },
  manager: {
    canManageUsers: true,
    canManageUnits: true,
    canManageTemplates: true,
    canViewReports: true,
    canCreateChecklists: true,
    canManageIncidents: true,
    canViewAuditTrail: true,
    canManageCompliance: false,
    canExportData: true,
    canManageSettings: false,
  },
  staff: {
    canManageUsers: false,
    canManageUnits: false,
    canManageTemplates: false,
    canViewReports: true,
    canCreateChecklists: true,
    canManageIncidents: true,
    canViewAuditTrail: false,
    canManageCompliance: false,
    canExportData: false,
    canManageSettings: false,
  },
  viewer: {
    canManageUsers: false,
    canManageUnits: false,
    canManageTemplates: false,
    canViewReports: true,
    canCreateChecklists: false,
    canManageIncidents: false,
    canViewAuditTrail: false,
    canManageCompliance: false,
    canExportData: false,
    canManageSettings: false,
  },
};

export const useRoleAccess = () => {
  const { user } = useAuth();

  const { data: userRole, isLoading } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data } = await supabase
        .from('user_roles')
        .select('role, tenant_id')
        .eq('user_id', user.id)
        .single();

      return data?.role as UserRole || 'viewer';
    },
    enabled: !!user?.id,
  });

  const permissions = useMemo(() => {
    if (!userRole) return ROLE_PERMISSIONS.viewer;
    return ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.viewer;
  }, [userRole]);

  const hasPermission = useMemo(() => {
    return (permission: keyof RolePermissions) => {
      return permissions[permission];
    };
  }, [permissions]);

  const isAtLeastRole = useMemo(() => {
    return (requiredRole: UserRole) => {
      if (!userRole) return false;
      
      const roleHierarchy: UserRole[] = ['viewer', 'staff', 'manager', 'admin', 'owner'];
      const currentIndex = roleHierarchy.indexOf(userRole);
      const requiredIndex = roleHierarchy.indexOf(requiredRole);
      
      return currentIndex >= requiredIndex;
    };
  }, [userRole]);

  return {
    userRole: userRole || 'viewer',
    permissions,
    hasPermission,
    isAtLeastRole,
    isLoading,
  };
};